import React, { useContext, useEffect, useState } from 'react';
import { Button, Form, Input, message, Popover } from 'antd';

import ScenarioCreationFormContext from '../../contexts/forms/scenarios/ScenarioCreationFormContext';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { FormCard } from '@leedpulse-monorepo/components';

import { DOMAINS_PAGE_PATH } from '../../pages/domains/DomainsPage';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

interface ScenarioCreationFormProps {
  domainId: string;
}

const ScenarioCreationForm: React.FC<ScenarioCreationFormProps> = ({
  domainId,
}) => {
  const navigate = useNavigate();
  const formContext = useContext(ScenarioCreationFormContext);
  const isBusy = formContext.status === ContextStatus.LOADING;
  const [nameInputStatus, setNameInputStatus] = useState<'' | "error">('');
  const [nameShortInputStatus, setNameShortInputStatus] = useState<'' | "error">('');

  useEffect(() => {
    if (formContext.idDomain === null) {
      formContext.initWithDomainId(domainId);
    }
  }, []);

  useEffect(() => {
    switch (formContext.status) {
      case ContextStatus.SUCCESS:
        message.success('Nouveau scénario créé avec succès');
        navigate(`${DOMAINS_PAGE_PATH}/${domainId}`);
        break;
      case ContextStatus.ERROR:
        // Synchro avec un systeme externe au rendu (DOM, storage, timer, toast,
        // navigation) : l'etat accompagne un effet de bord qui ne peut pas remonter au rendu.
        // eslint-disable-next-line react/set-state-in-effect
        setNameInputStatus('error');
        message.error(
          'Erreur lors de la création du scénario. Le nom ne doit pas être vide ou est invalide.'
        );
        break;
      default:
        break;
    }
  }, [formContext.status]);

  const handleNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    formContext.onNameChange(e.target.value);
    if (e.target.value.length === 0) {
      setNameInputStatus('error');
    } else {
      setNameInputStatus('');
    }
  };

  const handleNameShortChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    formContext.onNameShortChange(e.target.value);
    if (e.target.value.length === 0) {
      setNameShortInputStatus('error');
    } else {
      setNameShortInputStatus('');
    }
  };

  const renderHelpPopoverContent = () => {
    return (
      <div style={{ width: '325px' }}>
        <p>Lors de la création d'un scénario, seul le nom est requis. Une fois le scénario créé, veuillez compléter les autres champs (informations générales, questions à poser, prompts, etc.) depuis la page d'édition du scénario</p>
      </div>
    )
  };

  return (
    <FormCard
      title="Formulaire de création de scénario"
      showTitle={false}
      onCreated={() => formContext.submit()}
      isCreateLoading={isBusy}
      isOnError={
        [nameInputStatus, nameShortInputStatus].includes('error') ||
        (formContext.name === '' && formContext.nameShort === '')
      }
    >
      <Form layout="vertical">
        <Form.Item label="Nom">
          <Input
            maxLength={100}
            showCount
            placeholder="Acheteur - description"
            value={formContext.name}
            onChange={(e) => handleNameChanged(e)}
            disabled={isBusy}
            status={nameInputStatus}
            suffix={
              <Popover
                content={renderHelpPopoverContent}>
                <Button
                  type='text'
                  icon={<QuestionCircleOutlined />}
                />
              </Popover>
            }
          />
        </Form.Item>
        <Form.Item label="Nom court (affiché au client)">
          <Input
            maxLength={25}
            showCount
            placeholder="Acheteur"
            value={formContext.nameShort}
            onChange={(e) => handleNameShortChanged(e)}
            disabled={isBusy}
            status={nameShortInputStatus}
          />
        </Form.Item>
      </Form>
    </FormCard>
  );
};

export default ScenarioCreationForm;
